import React, { useState } from 'react';
import { Box, IconButton, Popover, Tooltip, Button } from '@mui/material';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import { addActivityReaction, removeActivityReaction } from '../../services/api';

interface ActivityReactionsProps {
  targetId: number;
  reactions: { [key: number]: number };
  userReaction: number | null;
  isPublic: boolean;
  onReactionUpdate?: (targetId: number, newReactions: { [key: number]: number }, newUserReaction: number | null) => void;
  addReaction?: (id: number, type: number) => Promise<any>;
  removeReaction?: (id: number) => Promise<any>;
}

// リアクションの絵文字マッピング
const reactionEmojis: { [key: number]: string } = {
  1: '👍',
  2: '❤️',
  3: '😆',
  4: '😍',
  5: '⚔️',
  6: '😱',
  7: '🚀',
  8: '🤣',
  9: '🎮',
  10: '🔥',
  11: '👏',
  12: '🎉',
  13: '😮',
  14: '😭',
  15: '👀',
  16: '💯',
  17: '🏆'
};

const reactionLabels: { [key: number]: string } = {
  1: 'いいね',
  2: 'ハート',
  3: '笑顔',
  4: 'ラブ',
  5: 'バトル',
  6: '恐怖',
  7: 'ロケット',
  8: '爆笑',
  9: 'ゲーム',
  10: '炎',
  11: '拍手',
  12: 'パーティー',
  13: '驚き',
  14: '泣き',
  15: '注目',
  16: '100点',
  17: 'トロフィー'
};

const ActivityReactions: React.FC<ActivityReactionsProps> = ({
  targetId,
  reactions,
  userReaction,
  isPublic,
  onReactionUpdate,
  addReaction = addActivityReaction,
  removeReaction = removeActivityReaction,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [localReactions, setLocalReactions] = useState(reactions || {});
  const [localUserReaction, setLocalUserReaction] = useState(userReaction);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleReactionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleReactionClose = () => {
    setAnchorEl(null);
  };

  const handleReactionButtonClick = (reactionType: number) => {
    // 既に選択中のリアクションをクリックした場合はポップオーバーを開く
    if (localUserReaction === reactionType) {
      // ダミーのイベントを作成してポップオーバーを開く
      const dummyEvent = {
        currentTarget: document.activeElement as HTMLButtonElement
      } as React.MouseEvent<HTMLButtonElement>;
      handleReactionClick(dummyEvent);
    } else {
      // 異なるリアクションの場合は直接変更
      handleReactionSelect(reactionType);
    }
  };

  const handleReactionSelect = async (reactionType: number) => {
    if (isProcessing) return;

    setIsProcessing(true);

    // 楽観的UI更新：先にローカル状態を更新
    const newReactions = { ...localReactions };

    // 既存のユーザーリアクションがあれば減らす
    if (localUserReaction && newReactions[localUserReaction]) {
      if (newReactions[localUserReaction] > 1) {
        newReactions[localUserReaction]--;
      } else {
        delete newReactions[localUserReaction];
      }
    }

    // 新しいリアクションを増やす
    newReactions[reactionType] = (newReactions[reactionType] || 0) + 1;

    setLocalReactions(newReactions);
    setLocalUserReaction(reactionType);

    // 親コンポーネントにも即座に通知（楽観的UI更新）
    if (onReactionUpdate) {
      onReactionUpdate(targetId, newReactions, reactionType);
    }

    handleReactionClose();

    try {
      // バックグラウンドでAPIリクエスト
      await addReaction(targetId, reactionType);
    } catch (error) {
      console.error('リアクションの処理に失敗しました:', error);
      // エラー時はロールバック
      setLocalReactions(reactions);
      setLocalUserReaction(userReaction);
      if (onReactionUpdate) {
        onReactionUpdate(targetId, reactions, userReaction);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReactionRemove = async () => {
    if (isProcessing || !localUserReaction) return;

    setIsProcessing(true);

    const removedReactionType = localUserReaction;

    // 楽観的UI更新：先にローカル状態を更新
    const newReactions = { ...localReactions };
    if (newReactions[removedReactionType] > 1) {
      newReactions[removedReactionType]--;
    } else {
      delete newReactions[removedReactionType];
    }

    setLocalReactions(newReactions);
    setLocalUserReaction(null);

    // 親コンポーネントにも即座に通知（楽観的UI更新）
    if (onReactionUpdate) {
      onReactionUpdate(targetId, newReactions, null);
    }

    handleReactionClose();

    try {
      // バックグラウンドでAPIリクエスト
      await removeReaction(targetId);
    } catch (error) {
      console.error('リアクションの削除に失敗しました:', error);
      // エラー時はロールバック
      setLocalReactions(reactions);
      setLocalUserReaction(userReaction);
      if (onReactionUpdate) {
        onReactionUpdate(targetId, reactions, userReaction);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      {/* 既存のリアクションを表示 */}
      {Object.entries(localReactions).map(([reactionType, count]) => (
        <Tooltip key={reactionType} title={reactionLabels[Number(reactionType)]}>
          <Box
            component={isPublic ? "button" : "div"}
            onClick={isPublic ? () => handleReactionButtonClick(Number(reactionType)) : undefined}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 'auto',
              px: 1,
              py: 0.5,
              fontSize: '0.75rem',
              borderRadius: 2,
              border: '1px solid',
              borderColor: localUserReaction === Number(reactionType) ? 'primary.main' : 'divider',
              backgroundColor: 'transparent',
              color: 'text.primary',
              cursor: isPublic ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '&:hover': isPublic ? {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              } : {},
              ...(isPublic && {
                '&:active': {
                  transform: 'scale(0.95)'
                }
              })
            }}
          >
            <span style={{ fontSize: '1rem' }}>{reactionEmojis[Number(reactionType)]}</span>
            <span>{count}</span>
          </Box>
        </Tooltip>
      ))}

      {/* リアクション追加ボタン（他人のページでのみ表示） */}
      {isPublic && (
        <>
          <Tooltip title="リアクションを追加">
            <IconButton
              size="small"
              onClick={handleReactionClick}
              disabled={isProcessing}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                width: 28,
                height: 28
              }}
            >
              <AddReactionOutlinedIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>

          {/* リアクション選択ポップオーバー */}
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleReactionClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: userReaction ? 1 : 0, maxWidth: 260 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map((type) => (
                  <Tooltip key={type} title={reactionLabels[type]}>
                    <IconButton
                      size="small"
                      onClick={() => handleReactionSelect(type)}
                      disabled={isProcessing}
                      sx={{
                        fontSize: { xs: '1.3rem', sm: '1.3rem' },
                        border: localUserReaction === type ? '1px solid' : 'none',
                        borderColor: 'primary.main',
                        '&:hover': {
                          transform: 'scale(1.2)',
                        },
                        transition: 'transform 0.2s'
                      }}
                    >
                      {reactionEmojis[type]}
                    </IconButton>
                  </Tooltip>
                ))}
              </Box>
              {localUserReaction && (
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                  <Button
                    size="small"
                    onClick={handleReactionRemove}
                    disabled={isProcessing}
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}
                  >
                    リアクションを取り消す
                  </Button>
                </Box>
              )}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

export default ActivityReactions;
